// HandoverAnalyticsCard — warm-start summary for a child received via a
// share code claim. Aggregates the previous therapist's activity so the
// receiving therapist gets a one-glance picture of what's been worked on.
// Silently renders nothing when the child was NOT received via handover.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { UserCheck, Calendar, Target, Sparkles, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

export default function HandoverAnalyticsCard({ childId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/children/${childId}/handover-analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setData(null); // 404 → child wasn't handed over
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (childId) load();
    return () => { cancelled = true; };
  }, [childId]);

  if (loading || !data) return null;

  return (
    <div
      className="mb-8 bg-gradient-to-br from-emerald-50 via-white to-teal-50 border-2 border-emerald-200 rounded-3xl overflow-hidden shadow-sm"
      data-testid="handover-analytics-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 hover:bg-emerald-50/50 transition"
        data-testid="handover-analytics-toggle"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
              Handover warm-start
            </p>
            <p className="font-serif text-lg text-stone-800">
              {data.previous_therapist?.name
                ? `Received from ${data.previous_therapist.name}`
                : 'Received from another therapist'}
            </p>
            <p className="text-xs text-stone-500">
              Handed over {formatDate(data.handed_over_at)} · {data.previous_sessions_count} session{data.previous_sessions_count === 1 ? '' : 's'} carried across
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-stone-400" /> : <ChevronDown className="w-5 h-5 text-stone-400" />}
      </button>

      {open && (
        <div className="p-5 pt-0 space-y-4">
          {/* Top-line stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl p-3 border border-emerald-100" data-testid="handover-stat-prev-sessions">
              <p className="text-xs text-stone-500 uppercase tracking-wider">Previous sessions</p>
              <p className="text-2xl font-serif text-stone-800">{data.previous_sessions_count}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 border border-emerald-100" data-testid="handover-stat-new-sessions">
              <p className="text-xs text-stone-500 uppercase tracking-wider">Your sessions</p>
              <p className="text-2xl font-serif text-stone-800">{data.new_sessions_count}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 border border-emerald-100" data-testid="handover-stat-velocity">
              <p className="text-xs text-stone-500 uppercase tracking-wider">Days to first session</p>
              <p className="text-2xl font-serif text-stone-800">
                {data.handover_velocity_days === null ? '—' : data.handover_velocity_days}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-3 border border-emerald-100" data-testid="handover-stat-range">
              <p className="text-xs text-stone-500 uppercase tracking-wider">Notes date range</p>
              <p className="text-xs text-stone-700 mt-1 leading-tight">
                <Calendar className="inline w-3 h-3 mr-1" />
                {formatDate(data.previous_notes_date_range?.first)}
                <br/>
                <Calendar className="inline w-3 h-3 mr-1" />
                {formatDate(data.previous_notes_date_range?.last)}
              </p>
            </div>
          </div>

          {/* Top goals */}
          {data.top_goals?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-800 font-semibold mb-2 flex items-center gap-1">
                <Target className="w-3.5 h-3.5" /> Goals most-worked-on by previous therapist
              </p>
              <ul className="space-y-1" data-testid="handover-top-goals">
                {data.top_goals.slice(0, 5).map((g) => (
                  <li key={g.goal} className="text-sm text-stone-700 bg-white rounded-lg px-3 py-1.5 border border-emerald-100 flex items-center justify-between">
                    <span className="truncate pr-3">{g.goal}</span>
                    <span className="text-xs text-emerald-700 font-medium flex-shrink-0">{g.sessions}× session{g.sessions === 1 ? '' : 's'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Top strategies */}
          {data.top_strategies?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-800 font-semibold mb-2 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Strategies that worked
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="handover-top-strategies">
                {data.top_strategies.slice(0, 8).map((s) => (
                  <span key={s.strategy} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white border border-emerald-100 text-stone-700">
                    {s.strategy}
                    <span className="text-emerald-600 font-medium">·{s.sessions}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Category mix */}
          {data.category_mix?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-800 font-semibold mb-2 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Session focus mix
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="handover-category-mix">
                {data.category_mix.map((c) => (
                  <span key={c.category} className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                    {c.category} · {c.sessions}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="pt-3 border-t border-emerald-100 text-xs text-stone-500 italic">
            💡 Tip: generate a progress report over this date range for a full narrative handover from the previous therapist.
          </div>
        </div>
      )}
    </div>
  );
}
